/**
 * @public — ported unchanged from the standalone tool, where this is exported but never actually
 * called by any workflow step. Kept for parity with the source rather than dropped as dead code.
 */
export async function normalizeImage(fileOrUrl: string | File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('No canvas context');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject('Failed to create blob');
      }, 'image/png');
    };
    img.onerror = reject;
    if (fileOrUrl instanceof File) {
      img.src = URL.createObjectURL(fileOrUrl);
    } else {
      img.src = fileOrUrl;
    }
  });
}

export async function generateTitlePageArtwork(
  logoBlob: Blob,
  theme: string,
  colors: { primary: string; secondary: string; accent: string; bg: string; darkText: string },
  config: {
    shapeColor: string;
    logoScale: number;
    logoPosition: string;
    logoX: number;
    logoY: number;
  },
): Promise<{ jpegBlob: Blob; pngBlob: Blob }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const W = 2100;
      const H = 2970;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('No canvas context');

      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16),
          g = parseInt(hex.slice(3, 5), 16),
          b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const baseColor = colors[config.shapeColor as keyof typeof colors] || colors.primary;
      const primary = baseColor;
      const accent = config.shapeColor === 'accent' ? colors.primary : colors.accent;
      const secondary = hexToRgba(baseColor, 0.6);
      const neutral = hexToRgba(baseColor, 0.2);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);

      if (theme === 'layered-wave') {
        ctx.fillStyle = secondary;
        ctx.fill(
          new Path2D(
            'M0 2860C508 2769 898 2552 1241 2201C1580 1854 1815 1568 2100 1072V2125C1800 2280 1581 2451 1363 2662C1235 2786 1112 2884 978 2970H0Z',
          ),
        );
        ctx.fillStyle = primary;
        ctx.fill(
          new Path2D('M723 2970C1116 2764 1401 2508 1617 2215C1824 1935 1961 1650 2100 1274V2970Z'),
        );
        ctx.strokeStyle = accent;
        ctx.lineWidth = 7;
        ctx.lineCap = 'round';
        ctx.stroke(new Path2D('M294 2970C814 2767 1214 2495 1519 2140'));
      } else if (theme === 'soft-horizon') {
        ctx.fillStyle = neutral;
        ctx.fill(
          new Path2D(
            'M0 1880C322 1765 570 1772 845 1830C1148 1894 1421 2000 1702 1989C1850 1983 1983 1923 2100 1815V2970H0Z',
          ),
        );
        ctx.fillStyle = primary;
        ctx.globalAlpha = 0.4;
        ctx.fill(
          new Path2D(
            'M0 2170C310 2060 596 2081 902 2153C1211 2226 1507 2291 1752 2190C1895 2131 2014 2022 2100 1919V2970H0Z',
          ),
        );
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = secondary;
        ctx.fill(
          new Path2D(
            'M0 2440C265 2362 535 2374 806 2440C1115 2516 1414 2582 1695 2474C1868 2407 2008 2276 2100 2160V2970H0Z',
          ),
        );
        ctx.fillStyle = primary;
        ctx.fill(
          new Path2D(
            'M0 2695C281 2636 578 2651 882 2716C1190 2782 1501 2825 1783 2705C1912 2650 2018 2570 2100 2488V2970H0Z',
          ),
        );
        ctx.globalAlpha = 1.0;
      } else if (theme === 'diagonal-sweep') {
        ctx.fillStyle = neutral;
        ctx.fill(new Path2D('M0 2950L2100 1310V1830L715 2970H0Z'));
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M715 2970L2100 1830V2315L1305 2970Z'));
        ctx.fillStyle = primary;
        ctx.fill(new Path2D('M1175 2970L2100 2208V2970Z'));
      } else if (theme === 'stepped-horizon') {
        ctx.fillStyle = neutral;
        ctx.fill(new Path2D('M0 2520H620V2320H1160V2110H1630V1880H2100V2970H0Z'));
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M0 2710H755V2520H1305V2320H1740V2110H2100V2970H0Z'));
        ctx.fillStyle = primary;
        ctx.globalAlpha = 0.55;
        ctx.fill(new Path2D('M0 2870H905V2710H1450V2520H1840V2320H2100V2970H0Z'));
        ctx.globalAlpha = 1.0;
      } else if (theme === 'corner-swell') {
        ctx.fillStyle = neutral;
        ctx.fill(
          new Path2D('M1340 2970C965 2782 713 2534 560 2225C413 1929 246 1748 0 1650V2970Z'),
        );
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M890 2970C613 2783 439 2572 340 2320C245 2078 133 1927 0 1868V2970Z'));
        ctx.fillStyle = primary;
        ctx.fill(new Path2D('M495 2970C295 2801 188 2619 131 2412C89 2257 45 2165 0 2128V2970Z'));
      } else if (theme === 'opposing-ribbons') {
        ctx.fillStyle = neutral;
        ctx.fill(new Path2D('M2100 0H730L2100 600Z'));
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M2100 0H1205L2100 390Z'));
        ctx.fillStyle = primary;
        ctx.fill(new Path2D('M2100 0H1600L2100 220Z'));
        ctx.fillStyle = accent;
        ctx.fill(new Path2D('M2100 0H1895L2100 92Z'));
        ctx.fillStyle = neutral;
        ctx.fill(new Path2D('M1370 2970H0V2370Z'));
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M895 2970H0V2580Z'));
        ctx.fillStyle = primary;
        ctx.fill(new Path2D('M500 2970H0V2750Z'));
        ctx.fillStyle = accent;
        ctx.fill(new Path2D('M205 2970H0V2878Z'));
      } else if (theme === 'corner-cut') {
        ctx.fillStyle = neutral;
        ctx.fill(new Path2D('M2100 1980L1390 2970H2100Z'));
        ctx.fillStyle = secondary;
        ctx.fill(new Path2D('M2100 2280L1605 2970H2100Z'));
        ctx.fillStyle = primary;
        ctx.fill(new Path2D('M2100 2580L1820 2970H2100Z'));
      } else if (theme === 'crescent-arc') {
        ctx.fillStyle = primary;
        ctx.fill(
          new Path2D(
            'M1780 0C1990 382 2100 810 2100 1285V2970H0V2800C481 2795 869 2694 1187 2495C1501 2298 1717 2017 1852 1654C1987 1292 2010 917 1922 561C1870 350 1805 163 1728 0Z',
          ),
        );
        ctx.fillStyle = accent;
        ctx.globalAlpha = 0.62;
        ctx.fill(
          new Path2D(
            'M1932 0C2045 292 2100 615 2100 970V2970H1585C1791 2712 1930 2424 2002 2106C2080 1761 2081 1424 2025 1081C1971 750 1874 390 1748 0Z',
          ),
        );
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = neutral;
        ctx.fill(
          new Path2D(
            'M1708 0C1907 415 1954 856 1847 1277C1741 1695 1514 2034 1164 2293C837 2534 449 2654 0 2655V2785C470 2780 874 2654 1216 2406C1594 2132 1840 1770 1956 1320C2074 861 2015 405 1818 0Z',
          ),
        );
        ctx.fillStyle = secondary;
        ctx.globalAlpha = 0.34;
        ctx.fill(
          new Path2D(
            'M1760 0C1952 415 1994 855 1882 1286C1771 1712 1538 2057 1180 2317C850 2556 455 2675 0 2676V2760C466 2757 865 2635 1201 2392C1573 2123 1815 1766 1929 1324C2047 866 1996 415 1803 0Z',
          ),
        );
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = '#ffffff';
        ctx.fill(
          new Path2D(
            'M1862 0C2028 396 2058 816 1947 1220C1835 1628 1610 1965 1267 2225C943 2471 560 2603 105 2630L0 2635V2668C473 2663 875 2533 1213 2282C1577 2011 1818 1659 1938 1233C2057 810 2019 389 1856 0Z',
          ),
        );
      }

      // Exact match to TitlePageStep.tsx logic
      const maxLogoW = W * (220 / 420); // 220px in 420px container
      const maxLogoH = H * (80 / 594); // 80px in 594px container

      const scW = maxLogoW / img.width;
      const scH = maxLogoH / img.height;
      const baseSc = Math.min(1, scW, scH);

      const logoSc = baseSc * (config.logoScale / 100);
      const lw = img.width * logoSc;
      const lh = img.height * logoSc;

      const cx = W * (config.logoX / 100);
      const cy = H * (config.logoY / 100);

      ctx.drawImage(img, cx - lw / 2, cy - lh / 2, lw, lh);

      canvas.toBlob(pngBlob => {
        if (!pngBlob) return reject('PNG generation failed');
        canvas.toBlob(
          jpegBlob => {
            if (!jpegBlob) return reject('JPEG generation failed');
            resolve({ pngBlob, jpegBlob });
          },
          'image/jpeg',
          0.9,
        );
      }, 'image/png');
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(logoBlob);
  });
}
